import React, { useEffect, useMemo, useState } from 'react';
import { Button, Form, Input, Label, Message, Modal, Pagination, Segment, Table, TextArea } from 'semantic-ui-react';
import { API, copy, isRoot, showError, showInfo, showSuccess, timestamp2string } from '../helpers';
import { ITEMS_PER_PAGE } from '../constants';
import { renderQuota, renderQuotaNumber } from '../helpers/render';

function renderStatus(status) {
  switch (status) {
    case 1:
      return <Label basic color='green'>启用</Label>;
    case 2:
      return <Label basic color='red'>禁用</Label>;
    default:
      return <Label basic color='black'>未知</Label>;
  }
}

const ThirdPartyAppsTable = () => {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [searching, setSearching] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [editingApp, setEditingApp] = useState(null);
  const [isEdit, setIsEdit] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    quota: 0,
    group: 'default',
    status: 1
  });

  const [showTopupModal, setShowTopupModal] = useState(false);
  const [topupApp, setTopupApp] = useState(null);
  const [topupAmount, setTopupAmount] = useState('100');
  const [topupNote, setTopupNote] = useState('');
  const [topupSubmitting, setTopupSubmitting] = useState(false);

  const [showSecretModal, setShowSecretModal] = useState(false);
  const [secretApp, setSecretApp] = useState(null);
  const [secretValue, setSecretValue] = useState('');
  const [secretLoading, setSecretLoading] = useState(false);

  const [historyKeyword, setHistoryKeyword] = useState('');
  const [topups, setTopups] = useState([]);
  const [topupsLoading, setTopupsLoading] = useState(true);
  const [topupsActivePage, setTopupsActivePage] = useState(1);

  const quotaPerUnit = useMemo(() => {
    const value = parseFloat(localStorage.getItem('quota_per_unit'));
    if (!value || value <= 0) return 500000;
    return value;
  }, []);

  const previewTopupQuota = useMemo(() => {
    const amountNum = parseFloat(topupAmount);
    if (!amountNum || amountNum <= 0) return 0;
    return Math.round(amountNum * quotaPerUnit);
  }, [topupAmount, quotaPerUnit]);

  const loadApps = async () => {
    const res = await API.get('/api/third_party_app/?p=0');
    const { success, message, data } = res.data;
    if (success) {
      setApps(data);
    } else {
      showError(message);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadApps().catch((reason) => {
      showError(reason);
    });
  }, []);

  const searchApps = async () => {
      if (searchKeyword === '') {
      loadApps();
      return;
    }
    setSearching(true);
    const res = await API.get(`/api/third_party_app/search?keyword=${searchKeyword}`);
    const { success, message, data } = res.data;
    if (success) {
      setApps(data);
    } else {
      showError(message);
    }
    setSearching(false);
  };

  const handleInputChange = (e, { name, value }) => {
    setFormData({ ...formData, [name]: value });
  };

  const openModal = (app = null) => {
    if (app) {
      setIsEdit(true);
      setEditingApp(app);
      setFormData({
        name: app.name,
        quota: app.quota,
        group: app.group || 'default',
        status: app.status
      });
    } else {
      setIsEdit(false);
      setEditingApp(null);
      setFormData({
        name: '',
        quota: 0,
        group: 'default',
        status: 1
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingApp(null);
  };

  const submitForm = async () => {
    if (!formData.name) {
      showError('请输入名称');
      return;
    }

    if (isEdit) {
      const res = await API.put('/api/third_party_app/', {
        id: editingApp.id,
        name: formData.name,
        quota: parseInt(formData.quota) || 0,
        group: formData.group || 'default',
        status: parseInt(formData.status) || 1
      });
      const { success, message } = res.data;
      if (success) {
        showSuccess('更新成功');
        closeModal();
        loadApps();
      } else {
        showError(message);
      }
    } else {
      const res = await API.post('/api/third_party_app/', {
        name: formData.name,
        group: formData.group || 'default'
      });
      const { success, message, data } = res.data;
      if (success) {
        showSuccess('创建成功！请妥善保管以下密钥：');
        showInfo(`App Key: ${data.app_key}\nApp Secret: ${data.app_secret}`, 10000);
        closeModal();
        loadApps();
      } else {
        showError(message);
      }
    }
  };

  const deleteApp = async (id) => {
    if (!window.confirm('确定要删除此应用吗？')) {
      return;
    }
    const res = await API.delete(`/api/third_party_app/${id}/`);
    const { success, message } = res.data;
    if (success) {
      showSuccess('删除成功');
      loadApps();
    } else {
      showError(message);
    }
  };

  const toggleStatus = async (app) => {
    const newStatus = app.status === 1 ? 2 : 1;
    const res = await API.put('/api/third_party_app/', {
      id: app.id,
      name: app.name,
      quota: app.quota,
      status: newStatus
    });
    const { success, message } = res.data;
    if (success) {
      showSuccess(newStatus === 1 ? '已启用' : '已禁用');
      loadApps();
    } else {
      showError(message);
    }
  };

  const openTopupModal = (app) => {
    setTopupApp(app);
    setTopupAmount('100');
    setTopupNote('');
    setShowTopupModal(true);
  };

  const closeTopupModal = () => {
    setShowTopupModal(false);
    setTopupApp(null);
  };

  const submitTopup = async () => {
    if (!topupApp) return;

    const amountNum = parseFloat(topupAmount);
    if (!amountNum || amountNum < 1 || amountNum > 10000) {
      showInfo('充值金额须在 1–10000 元之间');
      return;
    }

    setTopupSubmitting(true);
    try {
      const res = await API.post('/api/third_party_app/topup', {
        app_id: topupApp.id,
        amount_fen: Math.round(amountNum * 100),
        note: topupNote
      });
      const { success, message, data } = res.data;
      if (!success) {
        showError(message);
        return;
      }
      showSuccess(`已为应用「${topupApp.name}」充值 ${renderQuota(data.quota_balance)}`);
      closeTopupModal();
      loadApps();
      refreshTopupHistory();
    } catch (error) {
      showError(error);
    } finally {
      setTopupSubmitting(false);
    }
  };

  const openSecretModal = async (app) => {
    setSecretApp(app);
    setSecretValue('');
    setShowSecretModal(true);
    setSecretLoading(true);
    try {
      const res = await API.get(`/api/third_party_app/${app.id}/secret`);
      const { success, message, data } = res.data;
      if (success) {
        setSecretValue(data.app_secret);
      } else {
        showError(message);
      }
    } catch (error) {
      showError(error);
    } finally {
      setSecretLoading(false);
    }
  };

  const closeSecretModal = () => {
    setShowSecretModal(false);
    setSecretApp(null);
    setSecretValue('');
  };

  const loadTopups = async (startIdx, keyword = historyKeyword) => {
    const trimmedKeyword = keyword.trim();
    const res = await API.get(`/api/third_party_app/topup?p=${startIdx}&keyword=${encodeURIComponent(trimmedKeyword)}`);
    const { success, message, data } = res.data;
    if (success) {
      if (startIdx === 0) {
        setTopups(data);
      } else {
        let newTopups = [...topups];
        newTopups.splice(startIdx * ITEMS_PER_PAGE, data.length, ...data);
        setTopups(newTopups);
      }
    } else {
      showError(message);
    }
    setTopupsLoading(false);
  };

  const refreshTopupHistory = async (keyword = historyKeyword) => {
    setTopupsLoading(true);
    setTopupsActivePage(1);
    await loadTopups(0, keyword);
  };

  useEffect(() => {
    if (isRoot()) {
      refreshTopupHistory();
    }
  }, []);

  const onTopupsPaginationChange = (e, { activePage }) => {
    (async () => {
      if (activePage === Math.ceil(topups.length / ITEMS_PER_PAGE) + 1) {
        await loadTopups(activePage - 1);
      }
      setTopupsActivePage(activePage);
    })();
  };

  const rootUser = isRoot();

  return (
    <>
      <Form onSubmit={searchApps}>
        <Form.Group>
          <Form.Input
            placeholder='搜索应用名称...'
            name='keyword'
            value={searchKeyword}
            onChange={handleInputChange}
            control={Input}
          />
          <Form.Button content='搜索' loading={searching} />
        </Form.Group>
      </Form>

      <Message info>
        第三方应用用于通过 API 接口管理用户。请妥善保管 App Key 和 App Secret，Secret 仅在创建时显示一次。
        「额度余额」是应用可分配给其用户的额度池，root 用户可通过「充值」按钮为其充值。
      </Message>

      <Button content='添加应用' icon='add' primary onClick={() => openModal()} />

      <Table basic='very'>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>ID</Table.HeaderCell>
            <Table.HeaderCell>名称</Table.HeaderCell>
            <Table.HeaderCell>App Key</Table.HeaderCell>
            <Table.HeaderCell>额度余额</Table.HeaderCell>
            <Table.HeaderCell>默认配额</Table.HeaderCell>
            <Table.HeaderCell>默认分组</Table.HeaderCell>
            <Table.HeaderCell>状态</Table.HeaderCell>
            <Table.HeaderCell>创建时间</Table.HeaderCell>
            <Table.HeaderCell>操作</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {apps.map((app) => (
            <Table.Row key={app.id}>
              <Table.Cell>{app.id}</Table.Cell>
              <Table.Cell>{app.name}</Table.Cell>
              <Table.Cell>
                <code style={{ cursor: 'pointer' }} onClick={() => copy(app.app_key)}>
                  {app.app_key}
                </code>
              </Table.Cell>
              <Table.Cell>{renderQuota(app.quota_balance)}</Table.Cell>
              <Table.Cell>{app.quota}</Table.Cell>
              <Table.Cell>{app.group}</Table.Cell>
              <Table.Cell>{renderStatus(app.status)}</Table.Cell>
              <Table.Cell>{app.created_at}</Table.Cell>
              <Table.Cell>
                {rootUser && (
                  <Button size='tiny' color='green' onClick={() => openTopupModal(app)}>
                    充值
                  </Button>
                )}
                <Button size='tiny' color='teal' onClick={() => openSecretModal(app)}>
                  查看密钥
                </Button>
                <Button size='tiny' onClick={() => openModal(app)}>
                  编辑
                </Button>
                <Button
                  size='tiny'
                  color={app.status === 1 ? 'orange' : 'green'}
                  onClick={() => toggleStatus(app)}
                >
                  {app.status === 1 ? '禁用' : '启用'}
                </Button>
                <Button size='tiny' color='red' onClick={() => deleteApp(app.id)}>
                  删除
                </Button>
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>

      <Modal open={showModal} onClose={closeModal} size='small'>
        <Modal.Header>{isEdit ? '编辑应用' : '添加应用'}</Modal.Header>
        <Modal.Content>
          <Form>
            <Form.Input
              label='名称'
              name='name'
              value={formData.name}
              onChange={handleInputChange}
              placeholder='请输入应用名称'
            />
            <Form.Input
              label='默认配额'
              name='quota'
              type='number'
              value={formData.quota}
              onChange={handleInputChange}
              placeholder='新创建用户的默认配额'
            />
            <Form.Input
              label='默认分组'
              name='group'
              value={formData.group}
              onChange={handleInputChange}
              placeholder='新创建用户的默认分组'
            />
            {isEdit && (
              <Form.Select
                label='状态'
                name='status'
                value={formData.status}
                onChange={handleInputChange}
                options={[
                  { key: 1, text: '启用', value: 1 },
                  { key: 2, text: '禁用', value: 2 }
                ]}
              />
            )}
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={closeModal}>取消</Button>
          <Button primary onClick={submitForm}>
            {isEdit ? '保存' : '创建'}
          </Button>
        </Modal.Actions>
      </Modal>

      <Modal open={showTopupModal} onClose={closeTopupModal} size='small'>
        <Modal.Header>为应用「{topupApp?.name}」充值</Modal.Header>
        <Modal.Content>
          <Form>
            <Form.Group widths='equal'>
              <Form.Field>
                <label>转账金额（元）</label>
                <Input
                  type='number'
                  min='1'
                  max='10000'
                  step='0.01'
                  value={topupAmount}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === '' || val === '-') {
                      setTopupAmount('');
                      return;
                    }
                    const parts = val.split('.');
                    const formatted =
                      parts.length > 1 ? parts[0] + '.' + parts[1].slice(0, 2) : val;
                    setTopupAmount(formatted);
                  }}
                  label='¥'
                  labelPosition='left'
                />
              </Form.Field>
              <Form.Field>
                <label>预计发放额度</label>
                <div style={{ paddingTop: '9px' }}>
                  <Label size='large' color='teal'>
                    {previewTopupQuota > 0 ? renderQuotaNumber(previewTopupQuota) : '—'}
                  </Label>
                </div>
              </Form.Field>
            </Form.Group>
            <Form.Field
              control={TextArea}
              label='备注'
              placeholder='可选，例如银行流水号、确认人、备注信息'
              value={topupNote}
              onChange={(e) => setTopupNote(e.target.value)}
            />
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={closeTopupModal}>取消</Button>
          <Button color='green' onClick={submitTopup} loading={topupSubmitting} disabled={topupSubmitting}>
            确认充值
          </Button>
        </Modal.Actions>
      </Modal>

      <Modal open={showSecretModal} onClose={closeSecretModal} size='small'>
        <Modal.Header>App Secret — {secretApp?.name}</Modal.Header>
        <Modal.Content>
          {secretLoading ? (
            <p>加载中...</p>
          ) : (
            <Form>
              <Form.Field>
                <label>App Secret</label>
                <Input
                  value={secretValue}
                  readOnly
                  action={
                    <Button icon='copy' onClick={() => { copy(secretValue); showSuccess('已复制'); }} />
                  }
                />
              </Form.Field>
            </Form>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={closeSecretModal}>关闭</Button>
        </Modal.Actions>
      </Modal>

      {rootUser && (
        <Segment style={{ marginTop: '2em' }}>
          <Message info>
            root 用户可在确认线下到账后，为第三方应用充值额度。应用通过 OpenAPI 接口将额度分配给其用户。
          </Message>

          <Form>
            <Form.Group>
              <Form.Input
                fluid
                width={6}
                placeholder='搜索应用名 / 操作人'
                value={historyKeyword}
                onChange={(e, { value }) => setHistoryKeyword(value)}
              />
              <Form.Button fluid width={2} onClick={() => refreshTopupHistory(historyKeyword)} loading={topupsLoading}>
                查询
              </Form.Button>
            </Form.Group>
          </Form>

          <Table basic compact size='small'>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>时间</Table.HeaderCell>
                <Table.HeaderCell>应用</Table.HeaderCell>
                <Table.HeaderCell>操作人</Table.HeaderCell>
                <Table.HeaderCell>金额</Table.HeaderCell>
                <Table.HeaderCell>额度</Table.HeaderCell>
                <Table.HeaderCell>备注</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {topups
                .slice(
                  (topupsActivePage - 1) * ITEMS_PER_PAGE,
                  topupsActivePage * ITEMS_PER_PAGE
                )
                .map((topup) => (
                  <Table.Row key={topup.id}>
                    <Table.Cell>{timestamp2string(topup.created_at)}</Table.Cell>
                    <Table.Cell>{topup.app_name}</Table.Cell>
                    <Table.Cell>{topup.operator_username}</Table.Cell>
                    <Table.Cell>¥{(topup.amount_fen / 100).toFixed(2)}</Table.Cell>
                    <Table.Cell>{renderQuota(topup.quota, 6)}</Table.Cell>
                    <Table.Cell>{topup.note || '-'}</Table.Cell>
                  </Table.Row>
                ))}
            </Table.Body>
            <Table.Footer>
              <Table.Row>
                <Table.HeaderCell colSpan='6'>
                  <Pagination
                    floated='right'
                    activePage={topupsActivePage}
                    onPageChange={onTopupsPaginationChange}
                    size='small'
                    siblingRange={1}
                    totalPages={
                      Math.ceil(topups.length / ITEMS_PER_PAGE) +
                      (topups.length % ITEMS_PER_PAGE === 0 ? 1 : 0)
                    }
                  />
                </Table.HeaderCell>
              </Table.Row>
            </Table.Footer>
          </Table>
        </Segment>
      )}
    </>
  );
};

export default ThirdPartyAppsTable;
